import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
} from '@mui/material';
import dayjs from 'dayjs';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import {
  align_center,
  align_left,
  dataGridStyle,
  VM_Colors
} from '../../Styles/VMStyles';
import {
  ACCESS_RESTRICTED_MESSAGE,
  ADD,
  ROUTE_ADD_ENQUIRY,
  ROUTE_ENQUIRY_DETAILS,
  SESSION_USER_ID_KEY,
  SESSION_USER_ROLES_PERMISSIONS,
  UPDATE
} from '../../Util/VMConstants';
import { useNavigate } from 'react-router-dom';
import { API_GET_ENQUIRY_DATA, API_DELETE_ENQUIRY, API_GET_SUB_VENUELIST } from '../../Util/URLConstants';
import Loader from '../../Components/Loader';
import StorageFactory from '../../Util/StorageFactory';
import TitleWithAddButton from '../Home/TitleWithAddButton';
import StatusButton from '../../Components/StatusButton';
import { checkIsPermissionExist, renderSNo, toTitleCase, updateColorBasedOnStatus } from '../../Util/UtilHelper';
import { canMutateRecord, RECORD_OWNERSHIP_DENIED_MESSAGE } from '../../Util/recordAuthorization';
import MUITypography from '../../Components/MUITypography';
import VMFilters from '../../Components/VMFilters';
import NoPermission from '../../Components/NoPermission';
import ReusableDataGrid from '../../Components/ReusableDataGrid';
import CreatedByCell from '../../Components/Custom/CreatedByCell';

const formatSubVenueNames = (enq) => {
  const names = Array.isArray(enq?.event_sub_venue_names)
    ? enq.event_sub_venue_names.filter(Boolean)
    : [];
  if (names.length) return names.join(', ');

  const fromObjects = Array.isArray(enq?.event_sub_venues)
    ? enq.event_sub_venues.map((venue) => venue?.sub_venue_name).filter(Boolean)
    : [];
  return fromObjects.length ? fromObjects.join(', ') : '-';
};

const appendSubVenueFilter = (url, subVenueId) => {
  if (subVenueId && String(subVenueId).toLowerCase() !== 'all') {
    return `${url}&sub_venue_id=${encodeURIComponent(subVenueId)}`;
  }
  return url;
};

const VMAllEnquiries = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState({
    All: 0,
    Lead: 0,
    Confirmed: 0,
    'Not-interested': 0,
  });

  // Confirm delete dialog state
  const [deleteId, setDeleteId] = useState(null);

  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS);

  // 🎯 Fixed set of statuses for filtering
  const filterStatuses = ['All', 'Lead', 'Confirmed', 'Not-interested'];
  const [statusFilter, setStatusFilter] = useState('All');

  // 🔍 Search and 📅 Date filters (UI state)
  const [searchText, setSearchText] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [subVenues, setSubVenues] = useState([]);
  const [selectedSubVenueId, setSelectedSubVenueId] = useState('all'); // "all" = no filter

  // Applied filters (used in fetchData)
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    fromDate: null,
    toDate: null,
  });

  const fetchSubVenues = useCallback(async () => {
    if (!userID) return;
    try {
      const res = await fetch(`${API_GET_SUB_VENUELIST}${userID}`);
      const resp = await res.json();
      if (resp.status_code === 200) {
        const list = Array.isArray(resp.subvenue_info)
          ? resp.subvenue_info
          : Array.isArray(resp.data)
            ? resp.data
            : [];
        setSubVenues(list);
      } else {
        setSubVenues([]);
      }
    } catch (err) {
      console.error('Error fetching sub-venues:', err);
      setSubVenues([]);
    }
  }, [userID]);

  useEffect(() => {
    fetchSubVenues();
  }, [fetchSubVenues]);

  const canDelete = checkIsPermissionExist(VM_PERMISSIONS, 'VM004'); 

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_GET_ENQUIRY_DATA}?user_id=${userID}&page=${page}&no_of_rows=${pageSize}&status=${statusFilter}`;
      if (appliedFilters.search) url += `&search=${encodeURIComponent(appliedFilters.search)}`;
      if (appliedFilters.fromDate) url += `&from_date=${appliedFilters.fromDate}`;
      if (appliedFilters.toDate) url += `&to_date=${appliedFilters.toDate}`;
      url = appendSubVenueFilter(url, selectedSubVenueId);

      const res = await fetch(url);
      const resp = await res.json();

      if (resp.status_code === 200 && Array.isArray(resp.data)) {
        const formattedData = resp.data.map((enq, idx) => ({
          s_no: idx + 1 + page * pageSize,   
          id: enq.appointment_id || `ENQ${idx}`,
          appointment_id: enq.appointment_id,
          user_id: enq.user_id || '',
          name: enq.name || '',
          phone: `${enq.country_code || ''} ${enq.phone_no || ''}`.trim(),
          appointment_status: enq.appointment_status,
          // ✅ Format as DD/MM/YYYY for UI
          appointment_date: Array.isArray(enq.appointment_date)
            ? enq.appointment_date
                .map(dateStr => dayjs(dateStr).format('DD/MM/YYYY'))
                .join(', ')
            : '',
          notes: enq.notes || '',
          lead: enq.lead || '',
          created_by: enq.created_by || enq.createdBy || null,
          created_by_id: enq.created_by_id || enq.created_by?.user_id || enq.created_by?.userId || null,
          event_sub_venue_names: formatSubVenueNames(enq),
        }));

        setRows(formattedData);
        setTotalCount(resp.totalCount || 0);
      } else {
        toast.error(resp.message || 'Unexpected response from server');
        setRows([]);
      }
    } catch (err) {
      console.error('Error fetching enquiries:', err);
      toast.error('Failed to load enquiries');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, pageSize, selectedSubVenueId, statusFilter, userID]);

  const fetchStatusCounts = useCallback(async () => {
    try {
      const results = await Promise.all(
        filterStatuses.map(async (status) => {
          let url = `${API_GET_ENQUIRY_DATA}?user_id=${userID}&page=0&no_of_rows=1&status=${status}`;
          if (appliedFilters.search) url += `&search=${encodeURIComponent(appliedFilters.search)}`;
          if (appliedFilters.fromDate) url += `&from_date=${appliedFilters.fromDate}`;
          if (appliedFilters.toDate) url += `&to_date=${appliedFilters.toDate}`;
          url = appendSubVenueFilter(url, selectedSubVenueId);

          const res = await fetch(url);
          const resp = await res.json();
          const count = resp.status_code === 200 ? (resp.totalCount || 0) : 0;
          return [status, count];
        })
      );
      setStatusCounts(Object.fromEntries(results));
    } catch (err) {
      console.error('Error fetching enquiry status counts:', err);
    }
  }, [appliedFilters, selectedSubVenueId, userID]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchStatusCounts();
  }, [fetchStatusCounts]);

  const handleUpdate = (id) => {
    navigate('/' + ROUTE_ADD_ENQUIRY, { state: { enquiry_id: id, arrived: UPDATE } });
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const record = rows.find((r) => String(r.appointment_id) === String(deleteId));
    if (record && !canMutateRecord(VM_PERMISSIONS, 'VM004', record)) {
      toast.error(RECORD_OWNERSHIP_DENIED_MESSAGE);
      setDeleteId(null);
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${API_DELETE_ENQUIRY}?id=${deleteId}&user_id=${userID}`, {
        method: 'GET', // ideally DELETE
      });
      const resp = await res.json();

      if (resp.status_code === 200) {
        toast.success('Enquiry deleted successfully');
        fetchData();
        fetchStatusCounts();
      } else {
        toast.error(resp.message || 'Failed to delete enquiry');
      }
    } catch (err) {
      console.error('Error deleting enquiry:', err);
      toast.error('Something went wrong while deleting');
    } finally {
      setDeleteId(null);
      setLoading(false);
    }
  };

  const handleFilterChange = (status) => {
    setStatusFilter(status);
    setPage(0); 
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      search: searchText.trim(),
      fromDate: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : null, // ✅ send YYYY-MM-DD to DB
      toDate: toDate ? dayjs(toDate).format('YYYY-MM-DD') : null,       // ✅ send YYYY-MM-DD to DB
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setFromDate(null);
    setToDate(null);
    setSelectedSubVenueId('all');
    setAppliedFilters({ search: '', fromDate: null, toDate: null });
    setPage(0);
  };

  const handleSubVenueChange = (event) => {
    setSelectedSubVenueId(event.target.value);
    setPage(0);
  };

  const renderWrappedTextCell = (value, sx = {}) => (
    <Box
      component="span"
      sx={{
        display: 'flex',
        width: '100%',
        minHeight: '100%',
        alignItems: 'center',
        justifyContent: 'flex-start',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        lineHeight: 1.35,
        textAlign: 'left',
        ...sx,
      }}
    >
      {value || '-'}
    </Box>
  );

  const columns = [
    renderSNo(),
    {
      field: 'appointment_id',
      headerName: 'Enquiry ID',
      flex: 0.5,
      sortable: false,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Box sx={{ ...align_left, height: '100%', width: '100%', alignItems: 'center' }}>
          <Tooltip title="Redirect to Event Details" arrow>
            <MUITypography
            variant='body2'
              onClick={() =>
                navigate('/' + ROUTE_ENQUIRY_DETAILS, { state: { enquiry_id: params.value } })
              }
              sx={{
                cursor: 'pointer',
                color: 'primary.main',
                textDecoration: 'underline',
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                lineHeight: 1.35,
              }}
            >
              {params.value}
            </MUITypography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 0.5,
      sortable: false,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => renderWrappedTextCell(params.value),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 0.5,
      sortable: false,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => renderWrappedTextCell(params.value),
    },
    {
      field: 'appointment_date',
      headerName: 'Event Date',
      flex: 0.5,
      sortable: false,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => renderWrappedTextCell(params.value),
    },
    {
      field: 'event_sub_venue_names',
      headerName: 'Sub Venue',
      flex: 0.6,
      sortable: false,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => renderWrappedTextCell(params.value),
    },
    {
      field: 'lead',
      headerName: 'Lead Source',
      flex: 0.5,
      sortable: false,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => renderWrappedTextCell(params.value),
    },
    {
      field: 'appointment_status',
      headerName: 'Status',
      flex: 0.5,
      minWidth: 132,
      sortable: false,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => <StatusButton status={params.value} justify="left" />,
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      flex: 0.6,
      minWidth: 120,
      sortable: false,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const userObj = params.value || params.row?.created_by || null;
        return userObj ? (
          <Box sx={{ ...align_left, height: '100%', width: '100%', alignItems: 'center' }}>
            <CreatedByCell user={userObj} />
          </Box>
        ) : (
          renderWrappedTextCell('-')
        );
      },
    },
    // {
    //   field: 'enquiry_info',   
    //   headerName: 'View',
    //   flex: 0.4,
    //   sortable: false,
    //   align: 'left',
    //   headerAlign: 'left',
    //   renderCell: (params) => (
    //     <Box sx={{ ...align_left, height: '100%', width: '100%', ml: 0, alignItems: 'center' }}>
    //       <Tooltip title="Redirect to Event Details" arrow>
    //         <MUITypography
    //           onClick={() =>
    //             navigate('/' + ROUTE_ENQUIRY_DETAILS, { state: { enquiry_id: params.row.appointment_id } })
    //           }
    //           sx={{
    //             cursor: 'pointer',
    //             color: 'primary.main',
    //             textDecoration: 'underline',
    //           }}
    //         >
    //           View
    //         </MUITypography>
    //       </Tooltip>
    //     </Box>
    //   ),
    // },
  ];

  const canDeleteAnyRow =
    canDelete &&
    rows.some((row) => canMutateRecord(VM_PERMISSIONS, 'VM004', row));

  if (canDeleteAnyRow) {
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      flex: 0.4,
      // minWidth: 110,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ ...align_center, width: "100%", height: "100%", gap: 0.5 }}>
          {/* {canUpdate && (params.row.appointment_status.toLowerCase?.() !== 'confirmed' ) && (
            <Tooltip title="Edit Enquiry">
              <IconButton
                onClick={() => handleUpdate(params.row.appointment_id)}
                color="primary"
                aria-label="edit enquiry"
                size="small"
              >
                <EditNoteIcon />
              </IconButton>
            </Tooltip>
          )} */}
          {canMutateRecord(VM_PERMISSIONS, 'VM004', params.row) && (
            <Tooltip title="Delete Enquiry">
              <IconButton
                onClick={() => setDeleteId(params.row.appointment_id)}
                color="error"
                aria-label="delete enquiry"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
     
      ),
    });
  }

  return (
    <Box
      sx={{
        px: { xs: 2.5, sm: 3, md: 4 },
        py: { xs: 0, sm: 0 },
     
        // ml: { xs: 0, sm: 0, md: 0, lg: 5 },
        // p: { xs: , sm: 3, md: 4 }, // Add padding for better spacing
        overflowX: 'hidden',
      }}
    >
      <TitleWithAddButton
        title="All Enquiries"
        onAddClick={() =>
          navigate('/' + ROUTE_ADD_ENQUIRY, { state: { arrived: ADD } })
        }
        buttonLabel="Add Enquiry"
        isButtonDisplay={checkIsPermissionExist(VM_PERMISSIONS, 'VM001')}
      />
  
      {/* Filters Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' }, // Stack filters on smaller screens
          gap: 2,
          mb: 2,
          alignItems: { xs: 'stretch', sm: 'center' }, // Align filters properly
        }}
      >
        <VMFilters
          searchPlaceHolder={toTitleCase('Search by Name/Phone/ID')}
          searchValue={searchText}
          searchOnChange={(e) => setSearchText(e.target.value)}
          fromDateValue={fromDate}
          fromDateOnChange={setFromDate}
          toDateValue={toDate}
          toDateOnChange={setToDate}
          clearOnChange={handleClearFilters}
          applyOnChange={handleApplyFilters}
          isToalCountDisplay={false}
          totalCount={totalCount}
          extraFilters={
            <TextField
              select
              size="small"
              label="Sub Venue"
              value={selectedSubVenueId}
              onChange={handleSubVenueChange}
              sx={{
                width: 180,
                backgroundColor: VM_Colors.transparent,
              }}
            >
              <MenuItem value="all">All</MenuItem>
              {subVenues.map((venue) => (
                <MenuItem
                  key={venue.sub_venue_id}
                  value={String(venue.sub_venue_id)}
                >
                  {venue.sub_venue_name}
                </MenuItem>
              ))}
            </TextField>
          }
        />
      </Box>
  
      {checkIsPermissionExist(VM_PERMISSIONS, 'VM002') ? (
        <>
          {/* Status Chips */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mb: 2,
              overflowX: 'auto', // Enable horizontal scrolling for chips
            }}
          >
            {filterStatuses.map((status) => (
              <Chip
                key={status}
                label={`${status} (${statusCounts[status] ?? 0})`}
                color={status === statusFilter ? 'primary' : 'default'}
                onClick={() => handleFilterChange(status)}
                clickable
                sx={{
                  backgroundColor: updateColorBasedOnStatus(status),
                  color: VM_Colors.white,
                  fontWeight: status === statusFilter ? 'bold' : 'normal',
                  border: status === statusFilter ? '2px solid white' : 'none',
                  '&:hover': {
                    backgroundColor: updateColorBasedOnStatus(status),
                  },
                  mr:1
                }}
              />
            ))}
            <Box sx={{ ml: 'auto', paddingRight: '10px' }}>
              {/* <MUITypography>Count: {totalCount}</MUITypography> */}
            </Box>
          </Box>

          {/* DataGrid Section */}
          <Box
            sx={{
              overflowX: 'hidden',
            }}
          >
            <ReusableDataGrid
              rows={rows}
              columns={columns}
              rowCount={totalCount}
              pagination
              paginationMode="server"
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={({ page, pageSize }) => {
                setPage(page);
                setPageSize(pageSize);
              }}
              rowsPerPageOptions={[5, 10, 20, 50]}
              loading={loading}
              getRowHeight={() => 'auto'}
              disableSelectionOnClick
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              disableColumnMenu
              hideFooterSelectedRowCount
              autoHeight
              sx={{
                ...dataGridStyle,
                width: '100%',
                '& .MuiDataGrid-columnHeaderTitle': {
                  whiteSpace: 'normal',
                  lineHeight: 1.2,
                },
                '& .MuiDataGrid-cell': {
                  whiteSpace: 'normal !important',
                  lineHeight: '1.35 !important',
                  overflow: 'visible',
                  display: 'flex',
                  alignItems: 'flex-start',
                  py: 1,
                },
                '& .MuiDataGrid-cellContent': {
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  textOverflow: 'clip',
                },
              }}
            />
          </Box>
        </>
      ) : (
        <Box sx={{ mt: 4 }}>
          <NoPermission message={ACCESS_RESTRICTED_MESSAGE} />
        </Box>
      )}
  
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete enquiry <strong>{deleteId}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
  
      {/* <Loader loading={loading} /> */}
    </Box>
  );

};

export default VMAllEnquiries;
